/**
 * 角色继承可视化组件
 * 展示角色树形继承关系 + 选中角色的自有/继承权限对比
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, Tree, Spin, Empty, Tag, Space, Divider, Badge,
} from 'antd';
import {
  SafetyOutlined, LockOutlined, TeamOutlined,
} from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import { getRoleTree, getRoleInheritedPermissions, getRolePermissions } from '../../api/role';
import type { RoleTreeNode, InheritedPermission, Permission } from '../../api/types';

interface RoleInheritanceVizProps {
  visible: boolean;
  onClose: () => void;
}

// 将角色树转为 Ant Tree DataNode
const convertRoleTree = (nodes: RoleTreeNode[]): DataNode[] =>
  nodes.map((node) => ({
    key: node.id,
    title: (
      <Space size={4}>
        <span>{node.name}</span>
        <Tag style={{ fontSize: 11 }}>{node.code}</Tag>
        {node.userCount !== undefined && (
          <span style={{ color: '#999', fontSize: 12 }}>
            <TeamOutlined /> {node.userCount}
          </span>
        )}
      </Space>
    ),
    children: node.children?.length ? convertRoleTree(node.children) : undefined,
    // 存储原始数据方便取用
    roleData: node,
  } as DataNode & { roleData: RoleTreeNode }));

const collectAllKeys = (nodes: DataNode[]): string[] => {
  const keys: string[] = [];
  const traverse = (list: DataNode[]) => {
    for (const n of list) {
      keys.push(String(n.key));
      if (n.children?.length) traverse(n.children);
    }
  };
  traverse(nodes);
  return keys;
};

const RoleInheritanceViz: React.FC<RoleInheritanceVizProps> = ({ visible, onClose }) => {
  const [treeData, setTreeData] = useState<DataNode[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // 选中角色的权限
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [selectedRoleName, setSelectedRoleName] = useState('');
  const [ownPermissions, setOwnPermissions] = useState<Permission[]>([]);
  const [inheritedPermissions, setInheritedPermissions] = useState<InheritedPermission[]>([]);
  const [permLoading, setPermLoading] = useState(false);

  const fetchRoleTree = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await getRoleTree();
      const data = res?.data?.data || res?.data || [];
      const nodes = Array.isArray(data) ? data : data.list || [];
      const tree = convertRoleTree(nodes);
      setTreeData(tree);
      setExpandedKeys(collectAllKeys(tree));
    } catch {
      setTreeData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (visible) {
      fetchRoleTree();
      setSelectedRoleId(null);
      setOwnPermissions([]);
      setInheritedPermissions([]);
    }
  }, [visible, fetchRoleTree]);

  // 点击角色节点 → 加载权限
  const handleSelectRole = async (selectedKeys: React.Key[]) => {
    if (!selectedKeys.length) {
      setSelectedRoleId(null);
      return;
    }
    const roleId = String(selectedKeys[0]);
    setSelectedRoleId(roleId);

    // 从 tree 中找到角色名
    const findName = (nodes: DataNode[]): string => {
      for (const n of nodes) {
        if (String(n.key) === roleId) return (n as any).roleData?.name || '';
        if (n.children?.length) {
          const found = findName(n.children);
          if (found) return found;
        }
      }
      return '';
    };
    setSelectedRoleName(findName(treeData));

    setPermLoading(true);
    try {
      // 并行请求自有权限和继承权限
      const [ownRes, inheritedRes]: any[] = await Promise.allSettled([
        getRolePermissions(roleId),
        getRoleInheritedPermissions(roleId),
      ]);

      // 自有权限
      if (ownRes.status === 'fulfilled') {
        setOwnPermissions(ownRes.value?.data ?? []);
      } else {
        setOwnPermissions([]);
      }

      // 继承权限
      if (inheritedRes.status === 'fulfilled') {
        const d = inheritedRes.value?.data;
        const items = Array.isArray(d) ? d : d?.data || d?.list || [];
        setInheritedPermissions(items);
      } else {
        setInheritedPermissions([]);
      }
    } catch {
      setOwnPermissions([]);
      setInheritedPermissions([]);
    } finally {
      setPermLoading(false);
    }
  };

  const totalPerms = ownPermissions.length + inheritedPermissions.length;

  return (
    <Modal
      title="角色继承关系"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
    >
      <div style={{ display: 'flex', gap: 16 }}>
        {/* 左侧：角色树 */}
        <div style={{ width: 360, borderRight: '1px solid #f0f0f0', paddingRight: 16, minHeight: 400 }}>
          <div style={{ fontWeight: 600, marginBottom: 12 }}>角色层级</div>
          <Spin spinning={loading}>
            {treeData.length > 0 ? (
              <Tree
                treeData={treeData}
                expandedKeys={expandedKeys}
                onExpand={(keys) => setExpandedKeys(keys as string[])}
                onSelect={handleSelectRole}
                selectedKeys={selectedRoleId ? [selectedRoleId] : []}
                showLine={{ showLeafIcon: false }}
                blockNode
              />
            ) : (
              !loading && <Empty description="暂无角色数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Spin>
        </div>

        {/* 右侧：权限详情 */}
        <div style={{ flex: 1, minHeight: 400 }}>
          {selectedRoleId ? (
            <Spin spinning={permLoading}>
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 12 }}>
                {selectedRoleName}
              </div>
              <Space size="large" style={{ marginBottom: 16 }}>
                <Badge color="blue" text={<span>自有权限: <b>{ownPermissions.length}</b> 项</span>} />
                <Badge color="gray" text={<span>继承权限: <b>{inheritedPermissions.length}</b> 项</span>} />
                <span>合计: <b>{totalPerms}</b> 项</span>
              </Space>

              <Divider style={{ margin: '12px 0' }} />

              {/* 自有权限 */}
              {ownPermissions.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 500, marginBottom: 8 }}>
                    <SafetyOutlined style={{ marginRight: 4 }} />自有权限
                  </div>
                  <Space size={[4, 8]} wrap>
                    {ownPermissions.map((p) => (
                      <Tag color="blue" key={p.id}>{p.title || p.name} ({p.code})</Tag>
                    ))}
                  </Space>
                </div>
              )}

              {/* 继承权限 */}
              {inheritedPermissions.length > 0 && (
                <div>
                  <div style={{ fontWeight: 500, marginBottom: 8 }}>
                    <LockOutlined style={{ marginRight: 4, color: '#999' }} />继承权限
                  </div>
                  <Space size={[4, 8]} wrap>
                    {inheritedPermissions.map((p) => (
                      <Tag
                        key={`${p.id}-${p.sourceRoleId}`}
                        style={{ color: '#999', borderColor: '#d9d9d9', background: '#fafafa' }}
                      >
                        {p.name} ({p.code})
                        {p.sourceRoleName && (
                          <span style={{ fontSize: 11, marginLeft: 4, color: '#bbb' }}>
                            ← {p.sourceRoleName}
                          </span>
                        )}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}

              {totalPerms === 0 && !permLoading && (
                <Empty description="该角色暂无权限" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Spin>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
              请在左侧选择一个角色查看权限详情
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RoleInheritanceViz;
